import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
//@ts-ignore
import logoImg from '../../assets/logo-samdoc-topo.png';
import { ChevronDown, ChevronRight, Menu as MenuIcon, Search, LogOut, X } from 'lucide-react';
import { IMenuItem, menuItems } from './MenuData';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Contexts/AuthContext';
import PageTitle from '../PageTitle/PageTitle';

interface LayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<LayoutProps> = ({ children }) => {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [userName, setUserName] = useState<String>("")
  const [isMobileSearchVisible, setIsMobileSearchVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth <= 768) {
        setIsSidebarExpanded(false);
      }
    };
    const nome = localStorage.getItem("nome")
    setUserName(nome ?? "")
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const toggleSidebar = () => {
    setIsSidebarExpanded(!isSidebarExpanded);
  };

  const menuNavigate = (route?: string) => {
    if (route == null) return
    navigate(route)
  }
  const renderMenuItem = (item: IMenuItem) => {
    const isExpanded = expandedMenus.includes(item.id);
    const hasSubItems = item.subItems && item.subItems.length > 0;

    if(!item.visible){
      return (<></>)
    }

    return (
      <React.Fragment key={item.id}>
        <MenuItem
          onClick={() => hasSubItems ? toggleMenu(item.id) : menuNavigate(item.path)}
          isActive={isExpanded}
        >
          {item.icon}
          {(isSidebarExpanded || isMobile) && (
            <>
              <span>{item.label}</span>
              {hasSubItems && (
                isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
              )}
            </>
          )}
        </MenuItem>
        {isExpanded && hasSubItems && (isSidebarExpanded || isMobile) && (
          <div>
            {item.subItems?.map(subItem => {
              if(!subItem.visible){
                return(<></>)
              }
              return (
                <SubMenuItem key={subItem.id} onClick={subItem.visible ? () => menuNavigate(subItem.path) : ()=>{}}>
                  {subItem.label}
                </SubMenuItem>
              )
            })}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <LayoutContainer>
      <Navbar>
        <Logo>
          <MenuIcon
            size={24}
            onClick={toggleSidebar}
            style={{ cursor: 'pointer' }}
          />
          <img src={logoImg} alt="SAMDOC - Gerenciador de Documentos" />
        </Logo>
        <SearchBar>
          <Search size={18} color="#999" />
          <input placeholder="Buscar..." />
        </SearchBar>
        <MobileSearchButton onClick={() => setIsMobileSearchVisible(!isMobileSearchVisible)}>
          <Search size={20} />
        </MobileSearchButton>
        <LogOut onClick={logout} size={20} style={{ cursor: 'pointer' }} />
      </Navbar>

      <MobileSearchOverlay isVisible={isMobileSearchVisible}>
        <input
          type="text"
          placeholder="Buscar..."
          onBlur={() => setIsMobileSearchVisible(false)}
        />
      </MobileSearchOverlay>

      <MainContent>
        <Overlay
          isVisible={isSidebarExpanded && isMobile}
          onClick={() => setIsSidebarExpanded(false)}
        />
        <Sidebar isExpanded={isSidebarExpanded} isMobile={isMobile}>
          {isMobile && (
            <CloseButton onClick={() => setIsSidebarExpanded(false)}>
              <X size={24} /> Fechar Menu
            </CloseButton>
          )}
          <UserInfo>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FFF' }} />
            {(isSidebarExpanded || isMobile) && (
              <div>
                <div>Bem vindo,</div>
                <div>{userName}</div>
              </div>
            )}
          </UserInfo>

          <MenuSection>
            {menuItems.map(item => renderMenuItem(item))}
          </MenuSection>
        </Sidebar>
        <Content sidebarExpanded={isSidebarExpanded} isMobile={isMobile}>
          {children}
        </Content>
      </MainContent>
    </LayoutContainer>
  );
};

const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

const PageContainer = styled.div`
  padding: 20px;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 10px;
  }
`;

const LayoutContainer = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  `;

const Navbar = styled.nav`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    background-color: ${props => props.theme.cores.fundo.container.terciario};
    color: ${props => props.theme.cores.textoWhite};
    height: 50px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
  `;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.5rem;
  font-weight: bold;
  
  span {
    color: ${props => props.theme.cores.primaria};
  }
`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    background-color: ${props => props.theme.cores.fundo.input.secundario};
    border-radius: 4px;
    padding: 0.5rem;
    flex: 1;
    max-width: 600px;
    margin: 0 2rem;
    
    input {
      background: none;
      border: none;
      color: white;
      width: 100%;
      margin-left: 0.5rem;
      &::placeholder {
        color: ${props => props.theme.cores.fundo.input.placehold.secundario};
      }
      &:focus {
        outline: none;
      }
    }
  
    @media (max-width: ${breakpoints.mobile}) {
      display: none;
    }
  `;

const MainContent = styled.div`
    display: flex;
    flex: 1;
    margin-top: 50px; // Altura da navbar
  `;

const Overlay = styled.div<{ isVisible: boolean }>`
    display: none;
    
    @media (max-width: ${breakpoints.mobile}) {
      display: ${props => props.isVisible ? 'block' : 'none'};
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 100;
    }
  `;

const Sidebar = styled.aside<{ isExpanded: boolean; isMobile: boolean }>`
    width: ${props => props.isExpanded ? '250px' : '60px'};
    background-color: ${props => props.theme.cores.fundo.container.terciario};
    color: white;
    transition: all 0.3s ease;
    overflow: hidden;
    position: fixed;
    top: 50px;
    bottom: 0;
    left: ${props => props.isMobile && !props.isExpanded ? '-100%' : '0'};
    z-index: 200;
  
    @media (max-width: ${breakpoints.mobile}) {
      width: 280px;
    }
  `;

const CloseButton = styled.button`
    display: none;
    
    @media (max-width: ${breakpoints.mobile}) {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      color: ${props => props.theme.cores.textoWhite};
      padding: 1rem;
      width: 100%;
      cursor: pointer;
      
      &:hover {
        background-color: ${props => props.theme.cores.fundo.input.secundario};
      }
    }
  `;

const UserInfo = styled.div`
    background-color: ${props => props.theme.cores.primaria};
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  `;

const MenuSection = styled.div`
    padding: 1rem 0;
    overflow-y: auto;
    height: calc(100vh - 180px);

    /* Firefox */
    scrollbar-width: none; 

    /* Internet Explorer 10+ */
    -ms-overflow-style: none; 

    /* Chrome, Safari e Edge */
    &::-webkit-scrollbar {
      display: none;
    }
  `;

const MenuItem = styled.div<{ isActive?: boolean }>`
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    background-color: ${props => props.isActive ? props.theme.cores.fundo.input.secundario : 'transparent'};
    transition: background-color 0.2s;
    
    &:hover {
      background-color: ${props => props.theme.cores.fundo.input.secundario};
    }
  
    @media (max-width: ${breakpoints.mobile}) {
      padding: 1rem;
    }
  `;

const SubMenuItem = styled(MenuItem)`
    padding-left: 2.5rem;
    
    @media (max-width: ${breakpoints.mobile}) {
      padding-left: 3rem;
    }
  `;

const Content = styled.main<{ sidebarExpanded: boolean; isMobile: boolean }>`
    flex: 1;
    background-color: ${props => props.theme.cores.fundo.container.principal};
    padding: 1rem;
    margin-left: ${props => props.isMobile ? '0' : props.sidebarExpanded ? '250px' : '60px'};
    transition: margin-left 0.3s ease;
    min-height: calc(100vh - 50px);
  `;

const MobileSearchButton = styled.button`
    display: none;
    
    @media (max-width: ${breakpoints.mobile}) {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      color: ${props => props.theme.cores.textoWhite};
      padding: 0.5rem;
      cursor: pointer;
    }
  `;

const MobileSearchOverlay = styled.div<{ isVisible: boolean }>`
    display: none;
    
    @media (max-width: ${breakpoints.mobile}) {
      display: ${props => props.isVisible ? 'flex' : 'none'};
      position: fixed;
      top: 50px;
      left: 0;
      right: 0;
      padding: 1rem;
      background-color: ${props => props.theme.cores.fundo.container.terciario};
      z-index: 90;
      
      input {
        width: 100%;
        padding: 0.5rem;
        border: none;
        border-radius: 4px;
      }
    }
  `;

export default AppLayout;